import { SearchableSelect } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import DeletePocConfigRowConfirm from "../../../../components/modals/DeletePocConfigRowConfirm/deletePocConfigRowConfirm";
import type { ButtonWithDropdownMeta } from "../../../../data-model/customTableModel";
import { ModalId } from "../../../../enums/modalId";
import { useModalOpener } from "../../../../hooks/useModalOpener";
import { setOpenedModalId } from "../../../../redux/slice/modalSliceNew";
import { AppDispatch } from "../../../../redux/store/store";
import AddGroupModal from "./components/AddGroupModal";
import AddNewUserModal from "./components/AddNewUserModal";
import PocConfigurationFooter from "./components/PocConfigurationFooter";
import UpdateGroupModal from "./components/UpdateGroupModal";
import { useFilenameBatchOptions } from "./hooks/useFilenameBatchOptions";
import { useHealthFacilitySelector } from "./hooks/useHealthFacilitySelector";
import { usePocAssignmentGroupsMetadata } from "./hooks/usePocAssignmentGroupsMetadata";
import { usePocConfigTable } from "./hooks/usePocConfigTable";
import { usePocConfigTableMetadata } from "./hooks/usePocConfigTableMetadata";
import { usePocNameOptions } from "./hooks/usePocNameOptions";
import "./pocConfiguration.scss";
import {
  buildPocAssignmentTableHeaders,
  buildPocAssignmentTableRows,
  POC_FIELD_KEYS,
} from "./utils/buildPocAssignmentTableData";
import {
  buildGroupBadgeListFromNames,
  ROWS_PER_PAGE,
  TABLE_NAME,
} from "./utils/pocAssignmentConstants";

/**
 * Order assignment configuration page backed by the poc_config_table metadata doc.
 */
const PocConfiguration = () => {
  const dispatch = useDispatch<AppDispatch>();
  useModalOpener();

  const [pendingDeleteRow, setPendingDeleteRow] = useState<{
    id: string;
    pocLabel: string;
  } | null>(null);

  const {
    data: metadataDoc,
    loading: metadataLoading,
    error: metadataError,
  } = usePocConfigTableMetadata();
  const assignmentGroups = usePocAssignmentGroupsMetadata();
  const healthFacility = useHealthFacilitySelector();
  const table = usePocConfigTable(
    metadataDoc,
    healthFacility.selectedFacilityId,
    assignmentGroups.entries,
  );

  const existingPocs = useMemo(
    () => table.rows.map((row) => row.poc),
    [table.rows],
  );
  const pocNameOptions = usePocNameOptions(existingPocs);
  const filenameBatchOptions = useFilenameBatchOptions();

  const addUserModalGroupOptions = useMemo(
    () =>
      buildGroupBadgeListFromNames(
        assignmentGroups.entries.map((entry) => entry.group_name),
      ),
    [assignmentGroups.entries],
  );

  const tableHeaders = useMemo(() => buildPocAssignmentTableHeaders(), []);
  const tableData = useMemo(
    () =>
      buildPocAssignmentTableRows(
        table.filteredRows,
        table.isDirty,
        table.cohortBadgeList,
        table.groupBadgeList,
      ),
    [
      table.filteredRows,
      table.isDirty,
      table.cohortBadgeList,
      table.groupBadgeList,
    ],
  );

  /** Toolbar “Groups” control: Add opens the add-group modal; Update opens groups metadata. */
  const groupButtonDropdownMeta = useMemo<ButtonWithDropdownMeta[]>(
    () => [
      {
        text: "Groups",
        disabled: table.saving,
        buttonType: "secondary",
        size: "medium",
        menuData: [
          {
            id: "add-group",
            label: "Add",
            onClick: () => openModal(ModalId.ADD_GROUP_MODAL),
          },
          {
            id: "update-group",
            label: "Update",
            onClick: () =>
              dispatch(setOpenedModalId({ id: ModalId.UPDATE_GROUP_MODAL })),
          },
        ],
      },
    ],
    [table.saving, dispatch],
  );

  const hasGroup = (groupName: string) => {
    const trimmed = groupName.trim();
    if (!trimmed) return false;
    return (
      assignmentGroups.entries.some(
        (entry) => entry.group_name.trim() === trimmed,
      ) || table.groupBadgeList.some((group) => group.id === trimmed)
    );
  };

  /**
   * Routes editable cell updates back into the row state.
   *
   * @param rowData - Full shaped row from CustomTable.
   * @param fieldKey - Column key that changed.
   * @param newValue - Updated cell value.
   */
  const handleCellChange = (
    rowData: { id: string },
    fieldKey: string,
    newValue: unknown,
  ) => {
    if (!rowData?.id || !fieldKey) return;

    if (fieldKey === POC_FIELD_KEYS.batches && Array.isArray(newValue)) {
      table.updateBatches(rowData.id, newValue as string[]);
      return;
    }

    if (typeof newValue !== "string") return;

    if (fieldKey === POC_FIELD_KEYS.group) {
      table.updateGroup(rowData.id, newValue);
      return;
    }

    if (fieldKey === POC_FIELD_KEYS.availability) {
      table.updateAvailability(rowData.id, newValue);
      return;
    }

    if (fieldKey === POC_FIELD_KEYS.cohort) {
      table.updateCohort(rowData.id, newValue);
    }
  };

  /**
   * Opens the same confirm flow as Form Matrix: pending delete until user confirms.
   *
   * @param rowData - Row from CustomTable (includes `id` and POC string field).
   */
  const handleDeleteIconClick = (rowData: {
    id?: string;
    [key: string]: unknown;
  }) => {
    const id = rowData?.id;
    if (typeof id !== "string" || id === "") return;
    const pocRaw = rowData[POC_FIELD_KEYS.poc];
    const pocLabel =
      typeof pocRaw === "string" && pocRaw.trim() !== "" ? pocRaw : "This POC";
    setPendingDeleteRow({ id, pocLabel });
  };

  if (metadataLoading) {
    return (
      <div className="poc-configuration flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
        <div className="poc-configuration__container flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
          <LoaderMessage message="Loading POC configuration…" />
        </div>
      </div>
    );
  }

  if (metadataError) {
    return (
      <div className="poc-configuration flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
        <div className="poc-configuration__container flex h-full flex-col items-center justify-center gap-4 overflow-hidden rounded-lg bg-white p-6">
          <div className="text-small text-red-600">
            Failed to load POC configuration. Please try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="poc-configuration flex h-full min-h-0 flex-col bg-primaryGray-16 p-4">
      <div className="poc-configuration__container flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
        <div className="poc-configuration__header flex flex-shrink-0 flex-col gap-4 border-b border-primaryGray-16 pb-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-h11 font-bold text-primaryGray-1">
                Order Assignment Configuration
              </div>
              <div className="mt-1 text-small text-primaryGray-4">
                Manage POC assignments, groups, batches, and availability.
              </div>
            </div>
            <div className="w-full flex-shrink-0 sm:w-72">
              <SearchableSelect
                key={healthFacility.selectedFacilityId || "poc-health-facility"}
                id="poc-health-facility"
                options={healthFacility.options}
                label="Health Facility"
                placeholder="Select a Healthcare Facility"
                defaultValue={healthFacility.selectedFacilityId}
                onOptionChange={(option: { value: string }) =>
                  healthFacility.setSelectedFacilityId(option.value)
                }
              />
            </div>
          </div>
        </div>

        <div className="poc-configuration__content flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <div className="poc-configuration__table min-h-0 flex-1 overflow-y-auto">
            <CustomTable
              tableName={TABLE_NAME}
              tableHeaders={tableHeaders}
              tableData={tableData}
              count={tableData.length}
              totalCount={table.rows.length}
              itemsPerPage={ROWS_PER_PAGE}
              pagesPerView={5}
              isFetching={false}
              placeholder="Search by POC, group, or cohort"
              searchingText={table.setSearch}
              enableSearchOnChange
              showRefreshButton
              onRefreshButton={table.refresh}
              showOtherButton
              otherButtonMeta={[
                {
                  text: "Add New User",
                  onClick: () => openModal(ModalId.ADD_NEW_USER_MODAL),
                  disabled: table.saving,
                  buttonType: "primary",
                  size: "medium",
                },
              ]}
              showButtonWithDropdown
              buttonWithDropdownMeta={groupButtonDropdownMeta}
              onCellValueChange={handleCellChange}
              onDeleteIconClick={handleDeleteIconClick}
              disableHoverAndClick
              hidePagination={tableData.length === 0}
            />
          </div>
        </div>

        <PocConfigurationFooter
          dirtyCount={table.dirtyCount}
          saving={table.saving}
          onSave={table.saveChanges}
        />
      </div>

      <AddNewUserModal
        pocNameOptions={pocNameOptions}
        groupOptions={addUserModalGroupOptions}
        groupOptionsLoading={assignmentGroups.loading}
        onSave={table.addUser}
      />

      <AddGroupModal
        batchOptions={filenameBatchOptions.options}
        batchOptionsLoading={filenameBatchOptions.loading}
        hasGroup={hasGroup}
        onSave={async (data) => {
          await table.addGroup(data);
          await assignmentGroups.refetch();
        }}
      />

      <UpdateGroupModal onGroupsSaved={() => void assignmentGroups.refetch()} />

      <DeletePocConfigRowConfirm
        pocLabel={pendingDeleteRow?.pocLabel ?? null}
        rowId={pendingDeleteRow?.id ?? null}
        onConfirm={(rowId) => {
          table.removeRow(rowId);
        }}
        onClose={() => setPendingDeleteRow(null)}
      />
    </div>
  );
};

export default PocConfiguration;
